<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import type { BasketRepoError } from "@chatfood/dbe-client/errors";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import {
  AtomLink,
  AtomRadio,
  AtomText,
  AtomTextarea,
  SatButton,
  MolButtonMoney,
  OrgTipPicker,
  SatElasticPanel,
  SatLink,
  useToast,
  useDate,
  SatNavigation,
} from "@chatfood/design-system";
import { FetchError } from "ofetch";
import { computed, onMounted, ref } from "vue";
import { BasketClaims } from "@/components/basket-claims";
import { BasketEventName } from "@/components/basket-event-name";
import { BasketPax } from "@/components/basket-pax";
import { BasketSummary } from "@/components/basket-summary";
import { ChangeOrderingMode } from "@/components/change-ordering-mode";
import { GuestForm } from "@/components/guest-form";
import { CrossSellItems } from "@/components/cross-sell-items";
import { GuestPrice } from "@/components/guest-price";
import { I18nTranslate } from "@/components/i18n-translate";
import { useModalHubStore } from "@/components/modal-hub";
import { PackagingSelector } from "@/components/packaging-selector";
import { useBffErrors } from "~/composables/use-bff-errors";
import { useBusinessRulesValidation } from "~/composables/use-business-rules-validation";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import type { IDomainMoney } from "~/domain/money";
import type { IDomainPaymentMethod } from "~/domain/payment-method";
import type { IDomainPickupPackaging } from "~/domain/pickup-packaging";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useGuestStore } from "~/stores/guest";
import { useOutletStore } from "~/stores/outlet";
import { useAnalytics, useBugReporter } from "#imports";
import { useHeaderHeight } from "~/composables/use-header-height";
import { useRichCard } from "~/composables/use-rich-card";

const css = bemBuilder("pages-checkout");
const { t } = useLocale();
const { toastError } = useToast();
const { report } = useBugReporter();
const { errorsToString } = useBffErrors();
const { push, resolvePath, rawPush } = useNavigation();
const { formatTime, getReadableDate } = useDate();
const { paymentModeLabel } = useDomainLabel();
const { trackEvent } = useAnalytics();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const customer = useCustomerStore();
const guest = useGuestStore();
const modalHub = useModalHubStore();
const useFeatureMatrix = useFeatureMatrixStore();

const notes = ref(basket.notes ?? "");

const selectedPaymentMethod = ref<IDomainPaymentMethod>();
const selectedTip = ref<IDomainMoney>();
const showCrossSell = ref(true);

const showChangeOrderingModeModal = ref(false);

const defineSeoHeader = () => {
  const title = t("pages.checkout.seo_title", {
    businessName: business.name,
  });

  const description = t("pages.checkout.seo_description", {
    businessName: business.name,
  });

  const image = business.cover || business.logo;

  useRichCard({ title, description, image });
};

defineSeoHeader();

onMounted(() => {
  outlet.refreshPaymentMethods(basket.orderingMode, basket.salesChannel);
  trackEvent({
    name: "initiateCheckout",
    payload: {
      total: basket.total,
      discountCode: basket.discountCode,
      items: basket.items.map((item) => ({
        id: item.itemId,
        name: item.name,
        quantity: item.quantity,
        price: item.unitPrice,
      })),
    },
  });

  if (outlet.orderingMode.paymentMethods.length === 1) {
    onPaymentSelect(outlet.orderingMode.paymentMethods[0].id);
  }
});

const trackTipSelection = (amount: IDomainMoney) => {
  trackEvent({
    name: "selectTipAmount",
    payload: {
      amount,
    },
  });
};

const onTipSelect = (tip: IDomainMoney) => {
  selectedTip.value = tip;
  trackTipSelection(tip);
};

const onNotesChange = (value: string) => {
  basket.setNotes((notes.value = value));
};

const onPaymentSelect = (id: string) => {
  const payment = outlet.orderingMode.paymentMethods.find(
    (method) => method.id === id,
  );

  if (selectedTip.value) {
    selectedTip.value = undefined;
  }

  if (payment) {
    selectedPaymentMethod.value = payment;

    trackEvent({
      name: "addPaymentInfo",
      payload: {
        method: payment.name,
      },
    });

    return;
  }

  toastError(t("pages.checkout.error_selecting_payment_message"), {
    title: t("pages.checkout.error_selecting_payment_title"),
  });

  report("Payment method not found", {
    context: {
      availablePayments: outlet.orderingMode.paymentMethods,
      selectedPaymentMethodId: id,
    },
  });
};

const customerAddress = computed(() => {
  return customer.addresses.find((address) => address.id === basket.addressId);
});

const deliveryAddress = computed(
  () => customerAddress.value?.location ?? guest.address?.location,
);

const deliveryEta = computed(() => {
  if (basket.scheduledTime) {
    return t("pages.checkout.scheduled_delivery", {
      date: getReadableDate(basket.scheduledTime.day, "ll"),
      time: formatTime(basket.scheduledTime.time),
    });
  }

  const eta = outlet.orderingMode.estimatedCompletionMinutes;

  return t("pages.checkout.eta_delivery", {
    from: eta,
    to: eta + 10,
  });
});

const pickupEta = computed(() => {
  if (basket.scheduledTime) {
    return t("pages.checkout.scheduled_pickup", {
      date: getReadableDate(basket.scheduledTime.day, "ll"),
      time: formatTime(basket.scheduledTime.time),
    });
  }

  const eta = outlet.orderingMode.preparationMinutes;

  return t("pages.checkout.eta_pickup", {
    from: eta,
    to: eta + 10,
  });
});

const { resolveBasketException } = useBusinessRulesValidation();

const toggleOrderingModeModal = () => {
  showChangeOrderingModeModal.value = !showChangeOrderingModeModal.value;
};

const resolveBasketRepoError = (error: BasketRepoError): boolean => {
  if (!error.hasMeta()) return false;

  if (error.isBusinessRule()) {
    const errorMessage =
      error.cause instanceof Error
        ? error.cause.message
        : t("pages.checkout.place_order_error_message");

    const violation = error.meta.violation;

    if (violation === "delivery_service_unavailable") {
      showChangeOrderingModeModal.value = true;
      return true;
    }

    modalHub.hideCheckout();

    const errorData = {
      statusCode: 422,
      statusMessage: violation,
      message: errorMessage,
      data: {
        item_id: error.meta.context.itemId,
      },
    };

    return resolveBasketException("checkout", errorData);
  }

  if (error.isValidation()) {
    const errorData = {
      statusCode: 422,
      data: {
        fields: error.meta,
      },
    };

    return resolveBasketException("checkout", errorData);
  }

  return false;
};

const resolveFetchError = (error: FetchError): boolean => {
  if (
    error.statusCode === 422 &&
    error.data.statusMessage === "delivery_service_unavailable"
  ) {
    showChangeOrderingModeModal.value = true;
    return true;
  }

  modalHub.hideCheckout();

  return resolveBasketException("checkout", error.data);
};

const processOrder = async (
  paymentMethod: IDomainPaymentMethod,
  tipAmount: number,
) => {
  const packagingType = outlet.orderingMode.acceptsPackaging
    ? selectedPackagingType.value
    : undefined;

  try {
    const response = await basket.checkout(
      paymentMethod,
      tipAmount,
      packagingType,
    );

    trackEvent({
      name: "placeOrder",
      payload: {
        orderId: response.orderId,
        orderUid: response.orderUid,
        total: basket.total,
        tax: basket.totalBreakdown.taxAmount,
        deliveryFee: basket.totalBreakdown.deliveryFee,
        tips: {
          value: tipAmount,
          currency: basket.total.currency,
        },
        discount: basket.totalBreakdown.discountedAmount,
        loyalty: basket.totalBreakdown.loyaltyRedeemed,
        serviceCharges: basket.totalBreakdown.serviceCharges,
        bagFee: basket.totalBreakdown.bagFee,
        discountCode: basket.discountCode,
        orderingMode: basket.orderingMode,
        salesChannel: basket.salesChannel,
        items: basket.items.map((item) => ({
          id: item.itemId,
          name: item.name,
          quantity: item.quantity,
          price: item.unitPrice,
        })),
        packagingType,
      },
    });

    if (response.paymentUrl) {
      modalHub.showLoading(
        t("pages.checkout.place_order_redirect_title"),
        t("pages.checkout.place_order_redirect_message"),
      );

      window.location.href = response.paymentUrl;
      return;
    }

    trackEvent({
      name: "orderSuccess",
      payload: {
        orderId: response.orderId,
        orderUid: response.orderUid,
      },
    });

    await push("order-tracking", { trackingId: response.trackingId });

    basket.removeBasket();
    modalHub.hideLoading();
  } catch (error: unknown) {
    if (isBasketRepoError(error) && resolveBasketRepoError(error)) return;

    if (error instanceof FetchError && resolveFetchError(error)) return;

    const errorMessage =
      errorsToString(error, "\n") ??
      t("pages.checkout.place_order_error_message");

    modalHub.hideLoading();
    modalHub.showError(
      t("pages.checkout.place_order_error_title"),
      errorMessage,
      t("pages.checkout.place_order_error_cta_label"),
    );
  }
};

const onCheckout = () => {
  if (!customer.isLogged && !guest.isPersonalInfoElegibleToCheckout) {
    return toastError(t("pages.checkout.error_guest_info"));
  }

  if (!selectedPaymentMethod.value) {
    return toastError(t("pages.checkout.error_payment_required"));
  }

  modalHub.showLoading(
    t("pages.checkout.place_order_loading_title"),
    t("pages.checkout.place_order_loading_message"),
  );

  const tipAmount = selectedTip.value?.value || 0;

  processOrder(selectedPaymentMethod.value, tipAmount);
};

const displayTcs = computed(() => business.termsAndConditionsLink);

const basketTotal = computed(() => {
  if (!selectedTip.value) return basket.totalBreakdown.outstandingBalance;

  return {
    currency: basket.total.currency,
    value:
      basket.totalBreakdown.outstandingBalance.value + selectedTip.value.value,
  };
});

const onEditAddress = () => {
  modalHub.hideCheckout();

  modalHub.showAddressForm(() => {
    modalHub.showCheckout();
  });
};

const backRoute = computed(() => {
  return resolvePath("outlet", {
    outletSlug: outlet.slug,
    orderingMode: basket.orderingMode,
  });
});

const { headerHeightPixel } = useHeaderHeight();

const selectedPackagingType = ref<IDomainPickupPackaging>("takeaway");
</script>

<template>
  <div :class="css('')">
    <div :class="css('content')">
      <div :class="css('header-desktop')">
        <AtomText color="color-text-headline" type="h2">
          {{ t("pages.checkout.header_title") }}
        </AtomText>
        <SatButton
          variant="primary"
          theme="ghost"
          size="medium"
          :route="backRoute"
        >
          {{ t("pages.checkout.back_cta") }}
        </SatButton>
      </div>
      <div :class="css('header-mobile')">
        <SatNavigation
          icon-name-left="arrow-left"
          :sticky="false"
          @click-left-cta="() => rawPush(backRoute)"
        >
          <AtomText color="color-text-headline" type="h2">
            {{ t("pages.checkout.header_title") }}
          </AtomText>
        </SatNavigation>
        <div></div>
      </div>
      <div :class="css('overview')">
        <div :class="css('left-pane')">
          <div v-if="basket.isForDelivery" :class="css('section')">
            <AtomText type="subtitle">
              {{ t("pages.checkout.delivery_headline") }}
            </AtomText>
            <AtomText
              v-if="!deliveryAddress"
              color="color-error"
              data-test="no-address"
            >
              {{ t("pages.checkout.missing_address") }}
            </AtomText>
            <template v-else>
              <div :class="css('address')" data-test="address-info">
                <div>
                  <AtomText
                    v-if="customerAddress"
                    tag="div"
                    type="body-bold"
                    color="color-text-body"
                    data-test="address-nickname"
                  >
                    {{ customerAddress.nickname }}
                  </AtomText>
                  <AtomText
                    tag="div"
                    color="color-text-secondary"
                    data-test="address-location"
                  >
                    {{ deliveryAddress }}
                  </AtomText>
                </div>
                <div>
                  <AtomLink
                    to=""
                    data-test="edit-address"
                    @click.prevent="onEditAddress"
                  >
                    {{ t("pages.checkout.edit_address_label") }}
                  </AtomLink>
                </div>
              </div>
              <AtomText
                tag="div"
                data-test="deliver-eta"
                color="color-text-secondary"
              >
                {{ deliveryEta }}
              </AtomText>
            </template>
          </div>
          <div v-if="basket.isForPickup" :class="css('section')">
            <AtomText type="subtitle">
              {{ t("pages.checkout.pickup_headline") }}
            </AtomText>
            <div>
              <AtomText
                tag="div"
                type="body-bold"
                color="color-text-body"
                data-test="outlet-name"
              >
                {{ outlet.name }}
              </AtomText>
              <AtomText
                tag="div"
                color="color-text-secondary"
                data-test="outlet-location"
              >
                {{ outlet.address.location }}
              </AtomText>
            </div>
            <AtomText
              tag="div"
              color="color-text-secondary"
              data-test="pickup-eta"
            >
              {{ pickupEta }}
            </AtomText>
          </div>
          <div v-if="!customer.isLogged" :class="css('section')">
            <GuestForm />
          </div>
          <div
            v-if="business.isCatering"
            data-test="event-guest"
            :class="[css('section'), css('meta-fields')]"
          >
            <BasketEventName auto-save />
            <BasketPax auto-save />
          </div>
          <div :class="css('section')">
            <AtomText type="subtitle">
              {{ t("pages.checkout.payment_details") }}
            </AtomText>
            <AtomText
              v-if="!outlet.orderingMode.paymentMethods.length"
              color="color-error"
              data-test="no-payment-options"
            >
              {{ t("pages.checkout.missing_payment_methods") }}
            </AtomText>
            <template v-else>
              <AtomRadio
                v-for="payment in outlet.orderingMode.paymentMethods"
                :key="payment.id"
                name="payment-method"
                :value="payment.id"
                :checked="selectedPaymentMethod?.id === payment.id"
                :data-test="`payment-option-${payment.name}`"
                @change="onPaymentSelect"
              >
                {{ paymentModeLabel(payment.name) }}
              </AtomRadio>
            </template>
          </div>
          <Transition name="tips-effect">
            <div
              v-if="
                selectedPaymentMethod?.isOnline && business.config.isTipsEnabled
              "
              :class="css('section')"
            >
              <OrgTipPicker
                :title="t('pages.checkout.tip.title')"
                :subtitle="
                  basket.isForDelivery && business.config.acceptsDriverTips
                    ? t('pages.checkout.tip.subtitle_driver')
                    : t('pages.checkout.tip.subtitle_restaurant')
                "
                :no-tip-button-label="t('pages.checkout.tip.no_tip')"
                :bill-amount="basket.totalBreakdown.basketTotal"
                :tip-percentages="business.config.tipPercentages"
                :allow-custom="true"
                :custom-amount-label="t('pages.checkout.tip.custom_amount')"
                :selected-amount="selectedTip?.value"
                @change="onTipSelect"
              />
            </div>
          </Transition>
          <div
            v-if="outlet.orderingMode.acceptsPackaging"
            :class="css('section')"
          >
            <PackagingSelector v-model="selectedPackagingType" />
          </div>
          <div :class="css('section')">
            <BasketClaims />
          </div>
          <div
            v-if="useFeatureMatrix.isToggleEnabled('collect-order-notes')"
            :class="css('section')"
          >
            <AtomText>
              {{
                basket.isForPickup
                  ? t("pages.checkout.pickup_order_note_label")
                  : t("pages.checkout.delivery_order_note_label")
              }}
            </AtomText>
            <AtomTextarea
              :placeholder="t('pages.checkout.placeholder_order_note')"
              :auto-grow="true"
              :rows="2"
              :value="notes"
              data-test="notes"
              @change="onNotesChange"
            />
          </div>
        </div>
        <div :class="css('pane-divider')"></div>
        <div :class="css('right-pane')">
          <AtomText :class="css('right-pane-header')" type="h3">
            {{ t("pages.checkout.title") }}
          </AtomText>
          <div :class="css('right-pane-content')">
            <div :class="css('section')">
              <BasketSummary :tip-amount="selectedTip" />
            </div>
            <div
              v-if="
                showCrossSell &&
                useFeatureMatrix.isToggleEnabled(
                  'enable-inline-items-cross-sell',
                )
              "
              :class="[css('section'), css('section', 'no-inline-padding')]"
            >
              <AtomText
                tag="div"
                :class="css('cross-sell-title')"
                type="body-bold"
                >{{ t("pages.checkout.cross_sell_title") }}</AtomText
              >
              <CrossSellItems
                :class="css('cross-sell-scroller')"
                variant="scroll-x"
                @loaded="(length) => (showCrossSell = length > 0)"
              />
            </div>
          </div>
          <div :class="[css('right-pane-footer'), css('cta')]">
            <GuestPrice />
            <MolButtonMoney
              v-if="basket.items.length"
              :hide-cents="false"
              :locale="business.locale"
              size="large"
              data-test="checkout-cta"
              :price="basketTotal"
              @click="onCheckout"
            >
              {{ t("pages.checkout.cta_label") }}
            </MolButtonMoney>
            <div v-if="displayTcs">
              <AtomText
                color="color-text-secondary"
                type="small"
                data-test="guest-tcs"
              >
                <I18nTranslate keypath="pages.checkout.guest_tcs.sentence">
                  <template #link>
                    <SatLink
                      :href="business.termsAndConditionsLink"
                      target="_blank"
                      type="small"
                      underlined
                    >
                      {{ t("pages.checkout.guest_tcs.link") }}
                    </SatLink>
                  </template>
                </I18nTranslate>
              </AtomText>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div :class="[css('cta'), css('cta', 'mobile')]">
      <GuestPrice />
      <MolButtonMoney
        v-if="basket.items.length"
        :hide-cents="false"
        :locale="business.locale"
        size="large"
        data-test="checkout-cta"
        :price="basketTotal"
        @click="onCheckout"
      >
        {{ t("pages.checkout.cta_label") }}
      </MolButtonMoney>
      <div v-if="displayTcs">
        <AtomText
          color="color-text-secondary"
          type="small"
          data-test="guest-tcs"
        >
          <I18nTranslate keypath="pages.checkout.guest_tcs.sentence">
            <template #link>
              <SatLink
                :href="business.termsAndConditionsLink"
                target="_blank"
                type="small"
                underlined
              >
                {{ t("pages.checkout.guest_tcs.link") }}
              </SatLink>
            </template>
          </I18nTranslate>
        </AtomText>
      </div>
    </div>
    <SatElasticPanel
      v-model="showChangeOrderingModeModal"
      show-close
      width-fit-content
      padding="none"
    >
      <ChangeOrderingMode
        :class="css('selection')"
        :title="t('pages.checkout.change_ordering_mode_title')"
        :text="t('pages.checkout.change_ordering_mode_text')"
        @close="toggleOrderingModeModal"
        @change="modalHub.hideCheckout"
      />
    </SatElasticPanel>
  </div>
</template>

<style lang="scss" scoped>
/* calc(var(--spacing-2xl) * 2); */
$header-height: v-bind(headerHeightPixel);
$content-padding: 70px;

.tips-effect {
  &-enter-active {
    transition: all 250ms ease-out;
  }

  &-leave-active {
    transition: all 250ms ease-in;
  }

  &-enter-from,
  &-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }
}

.pages-checkout {
  position: relative;

  @media (width >= $breakpoint-sm) {
    padding: 0 var(--spacing-lg) var(--spacing-lg);
  }

  @media (width >= $breakpoint-lg) {
    padding: 0 var(--spacing-2xl) var(--spacing-2xl);
  }

  &__content {
    margin-inline: auto;
    width: 1300px;
    max-width: 100%;
  }

  &__header-desktop {
    padding-top: var(--spacing-lg);
    justify-content: space-between;
    display: none;

    @media (width > $breakpoint-sm) {
      display: flex;
    }
  }

  &__header-mobile {
    padding-top: var(--spacing-md);

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__overview {
    margin-top: var(--spacing-md);
    display: flex;
    flex-direction: column-reverse;
    justify-content: center;
    position: relative;

    @media (width >= $breakpoint-sm) {
      margin-bottom: var(--spacing-md);
      gap: var(--spacing-md);
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding: var(--spacing-md) var(--spacing-lg);

    &:not(:first-child) {
      border-top: solid 1px var(--color-base-95);
    }

    &--no-inline-padding {
      padding-inline: 0;
    }
  }

  &__meta-fields {
    @media (width >= $breakpoint-sm) {
      display: grid;
      flex-direction: row;
      grid-template-columns: 2fr 1fr;
    }
  }

  &__pane-divider {
    background-color: var(--color-base-95);
    height: 1px;
    width: 100%;
    display: block;
  }

  &__selection {
    max-width: 100%;

    @media (width > $breakpoint-sm) {
      width: 500px;
    }
  }

  &__address {
    display: flex;
    gap: var(--spacing-sm);
    align-items: flex-start;
    justify-content: space-between;
  }

  &__cta {
    display: flex;
    padding: var(--spacing-lg);
    gap: var(--spacing-sm);
    flex-direction: column;
    border-top: solid 1px var(--color-base-95);

    &--mobile {
      position: sticky;
      bottom: 0;
      background-color: var(--color-base-100);
      display: flex;

      @media (width >= $breakpoint-sm) {
        display: none;
      }
    }
  }

  &__left-pane,
  &__right-pane {
    background-color: var(--color-base-100);
    border-radius: var(--radii-card-lg);
  }

  &__left-pane {
    display: flex;
    flex-direction: column;
    height: fit-content;
  }

  $content: "";

  &__right-pane-header {
    display: none;

    @media (width >= $breakpoint-sm) {
      display: initial;
      padding: var(--spacing-lg) var(--spacing-lg) var(--spacing-sm);
    }
  }

  &__right-pane-content {
    @media (width >= $breakpoint-lg) {
      max-height: calc(100dvh - $header-height - $content-padding);
      overflow: auto;
      flex-grow: 1;
      overflow-y: auto;
    }

    @media (width >= $breakpoint-sm) {
      max-height: 66vh;
      overflow-y: scroll;
    }
  }

  &__right-pane-footer {
    display: flex;
    padding: var(--spacing-lg);
    gap: var(--spacing-sm);
    flex-direction: column;
    border-top: solid 1px var(--color-base-95);

    @media (width < $breakpoint-sm) {
      display: none;
    }
  }

  &__right-pane {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: fit-content;

    @media (width > $breakpoint-sm) {
      position: sticky;
      top: 85px;
    }
  }

  @media (width > $breakpoint-sm) {
    &__overview {
      flex-direction: row;
    }

    &__pane-divider {
      display: none;
    }

    &__left-pane {
      width: 58%;
    }

    &__right-pane {
      width: 42%;
    }
  }

  &__cross-sell-title {
    padding-inline: var(--spacing-lg);
  }

  &__cross-sell-scroller {
    padding-inline: var(--spacing-lg);
  }
}
</style>
